import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  CssBaseline,
  TextField,
  Grid,
  Link,
  Container,
  InputAdornment,
  IconButton,
  Paper,
  useTheme,
  CircularProgress,
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

import chatImage from "../../assets/multicanal.svg";
import WhatsAppIcon from "../../assets/logologinnovo.png";
import WhatsAppIconWhite from "../../assets/logoLoginWhite.png";
import { toast } from "react-toastify";

// --- Estilos atualizados ---
const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    //background: 'linear-gradient(135deg, #f77304 0%, #0e8def 70%)', // Mesmo gradiente do SignUp
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  leftSide: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  rightSide: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.12)',
    width: '100%',
    maxWidth: 420,
    position: 'relative',
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
  },
  logo: {
    height: 60,
    width: 'auto',
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  image: {
    maxWidth: '90%',
    height: 'auto',
    borderRadius: theme.spacing(2),
    boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.16)',
  },
  link: {
    fontWeight: 500,
  },
}));

// --- Componente Logo (reutilizável) ---
const Logo = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDarkMode = theme.palette.type === 'dark';
  return (
    <img
      src={isDarkMode ? WhatsAppIconWhite : WhatsAppIcon}
      alt="Logo"
      className={classes.logo}
    />
  );
};

// --- Componente Principal ---
const Login = () => {
  const classes = useStyles();
  const [user, setUser] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = (e) => {
    const {name, value} = e.target;
    setUser({
      ...user, 
      [name]: name === "email" ? value.toLowerCase() : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await handleLogin(user);
    } catch (err) {
      toast.error("Erro ao fazer login. Verifique suas credenciais.");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("expired") === "true") {
      if (!toast.isActive("session-expired")) {
        toast.warn("Sessão expirada. Por favor, faça login novamente.", {
          toastId: "session-expired",
        });
      }
      // Remove param da URL
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete("expired");
      window.history.replaceState({}, "", newUrl);
    }
  }, []);


  return (
    <div className={classes.root}>
      <CssBaseline />

      {/* Lado Esquerdo - Formulário */}
      <div className={classes.leftSide}>
        <Container component="main" maxWidth="xs">
          <Paper className={classes.paper} elevation={6}>
            <div className={classes.logoContainer}>
              <Logo />
            </div>

            {/* <Typography component="h1" variant="h5" align="center" gutterBottom>
              {i18n.t('login.title') || 'Bem-vindo'}
            </Typography> */}

            <form className={classes.form} noValidate onSubmit={handleSubmit}>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="email"
                label={i18n.t('login.form.email')}
                name="email"
                value={user.email}
                onChange={handleChangeInput}
                autoComplete="email"
                autoFocus
                required
              />
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                name="password"
                label={i18n.t('login.form.password')}
                id="password"
                value={user.password}
                onChange={handleChangeInput}
                autoComplete="current-password"
                type={showPassword ? 'text' : 'password'}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? i18n.t('login.buttons.loading') : i18n.t('login.buttons.submit')}
              </Button>

              <Grid container justifyContent="flex-end">
                <Grid item>
                  <Link
                    component={RouterLink}
                    to="/signup"
                    variant="body2"
                    className={classes.link}
                  >
                    {i18n.t('login.buttons.register')}
                  </Link>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Container>
      </div>

      {/* Lado Direito - Imagem */}
      <div className={classes.rightSide}>
        <img src={chatImage} alt="Chat Ilustrativo" className={classes.image} />
      </div>
    </div>
  );
};

export default Login;